import { Component, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from 'ngx-intl-tel-input';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { routes } from '../../../shared/routes/routes';
import { CommonService } from '../../../shared/common/common.service';
import { ApiService } from '../../../api.service';
import moment from 'moment';

@Component({
  standalone: false,
  selector: 'app-profile-settings',
  templateUrl: './profile-settings.component.html',
  styleUrls: ['./profile-settings.component.scss'],
})
export class ProfileSettingsComponent implements OnInit {
  @Input() isOpenFromPopup?: boolean = false;

  uploadedFile: any;
  previewImage: string | null = null;
  userProfile: any = {};
  countryDetails: any = [];
  routes = routes;
  userProfileForm: any;
  myDateValue = new Date();
  displayName: any;
  selectedCountryStates: any[] = [];
  selectedStateCities: any[] = [];
  separateDialCode = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];
  dobValue: any = '';
  isDobDisabled: boolean = false;
  eligibilityStatus: 'ELIGIBLE' | 'INELIGIBLE' | 'INACTIVE' = 'INACTIVE';
  activeDays: any = 0;

  formFields = [
    {
      name: 'first_name',
      label: 'First Name',
      placeholder: 'Enter First Name',
    },
    { name: 'last_name', label: 'Last Name', placeholder: 'Enter Last Name' },
    {
      name: 'dob',
      label: 'Date of Birth',
      maxLength: '9999-12-31',
      minLength: '1900-01-01',
      type: 'date',
      placeholder: 'Select Date of Birth',
    },
    {
      name: 'blood_group',
      label: 'Blood Group',
      placeholder: 'Select Blood Group',
    },
    {
      name: 'email',
      label: 'Email ID',
      type: 'email',
      placeholder: 'Enter Email ID',
    },
    { name: 'height', label: 'Height (cm)', placeholder: 'e.g., 150' },
    { name: 'weight', label: 'Weight (kg)', placeholder: 'e.g., 70.75' },
    { name: 'gender', label: 'Gender', placeholder: 'Select Gender' },
    {
      name: 'phone',
      label: 'Mobile Number',
      placeholder: 'Enter Mobile Number',
      isPhone: true,
    },
    {
      name: 'alt_phone_number',
      label: 'Alternate Mobile Number',
      placeholder: 'Enter Alternate Mobile Number',
    },
    {
      name: 'caregiver',
      label: 'Assign Caregiver',
      placeholder: 'Select Caregiver',
    },
    { name: 'address', label: 'Address', placeholder: 'Enter Address' },
    { name: 'landmark', label: 'Landmark', placeholder: 'Enter Landmark' },
    { name: 'country', label: 'Country', placeholder: 'Enter Country' },
    { name: 'state', label: 'State', placeholder: 'Enter State' },
    { name: 'city', label: 'City', placeholder: 'Enter City' },
  ];
  subDetail: any;
  bsDatepickerConfig: any;
  assignedCareGiver: any;

  constructor(
    private commonService: CommonService,
    private fb: FormBuilder,
    private router: Router,
    private apiService: ApiService,
    private tosterService: ToastrService,
    private spinner: NgxSpinnerService,
  ) {}

  ngOnInit() {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      // this.checkEligibility();
      this.getAllCountries();
    }

    this.subDetail = JSON.parse(localStorage.getItem('subDetail') || '{}');
    this.bsDatepickerConfig = {
      showWeekNumbers: false,
      minDate: moment().subtract(65, 'years').toDate(),
      maxDate: moment().subtract(18, 'years').toDate(),
      dateInputFormat: 'YYYY-MM-DD',
      adaptivePosition: true,
      returnFocusToInput: true,
      selectFromOtherMonth: true,
    };

    this.userProfileForm = this.fb.group({
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      dob: ['', [Validators.required, this.dobNotInFutureValidator()]],
      blood_group: ['', Validators.required],
      profile: [''],
      gender: ['', Validators.required],
      email: ['', [Validators.required, this.emailValidator()]],
      height: ['', [Validators.required, this.positiveNumberValidator()]],
      weight: ['', [Validators.required, this.positiveNumberValidator()]],
      phone: [
        null,
        [
          Validators.required,
          this.zimbabweMobileValidator(),
          this.noAllZerosPhoneValidator(),
        ],
      ],
      alt_phone_number: [
        null,
        [
          this.zimbabweAlternateMobileValidator(),
          this.noAllZerosPhoneValidator(),
        ],
      ],
      address: ['', Validators.required],
      landmark: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      country: ['', Validators.required],
    });

    this.commonService.selectedUser$.subscribe((user) => {
      if (user && this.isOpenFromPopup) {
        this.resetFormValidation();
        this.userProfile = user || {};
        this.getAllCountries(this.userProfile.user_id);
        this.setUserProfile(false);
      } else {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const localUser = JSON.parse(userStr);
          this.getUserProfile(localUser.user_id);
        }
      }
    });

    this.commonService.resetProfileForm$.subscribe(() => {
      this.resetFormValidation();
    });

    this.handleCaregiverOtpSuccess();
  }

  handleDateOfBirthSelection(ev: any) {
    if (!ev) return;

    const selectedDate = moment.isMoment(ev) ? ev : moment(ev);
    const age = moment().diff(selectedDate, 'years');

    if (this.userProfile?.relationship_type !== 'CHILD') {
      if (age < 18) {
        this.tosterService.error('You must be at least 18 years old');
        this.userProfileForm?.get('dob')?.reset();
        return;
      }
    }

    if (age > 65) {
      this.tosterService.error('Age should be less than or equal to 65 years');
      this.userProfileForm?.get('dob')?.reset();
      return;
    }

    const updatedDate = selectedDate.format('YYYY-MM-DD');
    this.userProfileForm?.get('dob')?.setValue(updatedDate);
  }

  nameLengthValidator = (): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors | null => {
      const firstName = control.get('first_name')?.value || '';
      const lastName = control.get('last_name')?.value || '';

      const combined = (firstName + lastName).replace(/[^a-zA-Z]/g, '');

      if (combined.length > 35) {
        return { nameLengthExceeded: true };
      }

      return null;
    };
  };

  dobNotInFutureValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const dob = new Date(control.value);
      const today = new Date();
      if (control.value && dob > today) {
        return { futureDate: true };
      }
      return null;
    };
  }

  cleanNameInput(event: any) {
    const value = event.target.value.replace(/[^a-zA-Z\s]/g, '');
    event.target.value = value;
  }

  zimbabweMobileValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let value = control?.value?.nationalNumber || '';
      value = value.replace(/[\s()-]/g, '');
      value = value.startsWith('0') ? value.substring(1) : value;
      const zimbabweMobileRegex = /^\d{9}$/;
      return zimbabweMobileRegex.test(value)
        ? null
        : { invalidZimbabweMobile: true };
    };
  }

  positiveNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value === null || value === '') {
        return null;
      }

      const num = parseFloat(value);

      if (isNaN(num)) {
        return { invalidNumber: true };
      }

      if (num <= 0) {
        return { invalidZero: true };
      }

      return null;
    };
  }

  handleCaregiverOtpSuccess() {
    this.commonService.assignCaregiverValidate$.subscribe((resp: any) => {
      if (resp) {
        this.assignedCareGiver = resp;
      }
    });
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string;
      if (!value) return null;
      if (value.length > 64) return { emailTooLong: true };
      const [localPart, domain] = value.split('@');
      if (!domain) return { emailInvalid: true };
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
      return emailRegex.test(value) ? null : { emailInvalid: true };
    };
  }

  zimbabweAlternateMobileValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = (control?.value || '').toString().replace(/\s+/g, '');

      if (!value) return null;

      if (value.startsWith('0')) {
        return /^\d{10}$/.test(value) ? null : { invalidZimbabweMobile: true };
      }

      return /^\d{9}$/.test(value) ? null : { invalidZimbabweMobile: true };
    };
  }

  onPhoneInput(event: any): void {
    if (this.userProfile?.phone) {
      this.userProfileForm.patchValue(
        {
          phone: {
            number: this.userProfile.phone,
            internationalNumber: this.userProfile.phone,
            nationalNumber: this.userProfile.phone.startsWith('0')
              ? this.userProfile.phone.substring(1)
              : this.userProfile.phone,
            countryCode: 'ZW',
            dialCode: '+263',
          },
        },
        { emitEvent: false },
      );
      return;
    }

    if (event && typeof event === 'object') {
      const nationalNumber = event?.nationalNumber?.replace(/\D/g, '') || '';

      this.userProfileForm.patchValue(
        {
          phone: {
            ...event,
            nationalNumber,
          },
        },
        { emitEvent: false },
      );
    }
  }

  onlyAllowNumbers(event: KeyboardEvent, fieldName: string) {
    const input = event.target as HTMLInputElement;
    const allowedKeys = [
      'Backspace',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Delete',
    ];

    if (fieldName === 'weight') {
      if (!/^\d$/.test(event.key) && event.key !== '.') {
        event.preventDefault();
        return;
      }

      if (event.key === '.' && input.value.includes('.')) {
        event.preventDefault();
        return;
      }

      if (input.value.includes('.')) {
        if (input.value.length >= 5 && !allowedKeys.includes(event.key)) {
          event.preventDefault();
        }
      } else {
        if (
          input.value.length >= 3 &&
          event.key !== '.' &&
          !allowedKeys.includes(event.key)
        ) {
          event.preventDefault();
        }
      }
    }

    if (fieldName === 'height') {
      if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
        event.preventDefault();
        return;
      }

      if (input.value.length >= 3 && !allowedKeys.includes(event.key)) {
        event.preventDefault();
      }
    }
    if (fieldName === 'alt_phone_number') {
      if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
        event.preventDefault();
      }

      const currentValue = input.value + event.key;
      if (currentValue.startsWith('0')) {
        if (currentValue.length > 10) {
          event.preventDefault();
        }
      } else {
        if (currentValue.length > 9) {
          event.preventDefault();
        }
      }
    }
  }

  sanitizeInput(event: Event, fieldName: string) {
    const input = event.target as HTMLInputElement;
    let cleaned = input.value;

    if (fieldName === 'weight') {
      cleaned = cleaned.replace(/[^0-9.]/g, '');
      const parts = cleaned.split('.');

      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts[1];
      }

      if (cleaned.includes('.')) {
        cleaned = cleaned.slice(0, 5);
      } else {
        cleaned = cleaned.slice(0, 3);
      }
    }

    if (fieldName === 'height') {
      cleaned = cleaned.replace(/[^0-9]/g, '');
      cleaned = cleaned.slice(0, 3);
    }

    input.value = cleaned;
    this.userProfileForm
      .get(fieldName)
      ?.setValue(cleaned, { emitEvent: false });
  }

  numberWithDecimalValidator() {
    return (control: any) => {
      const value = control.value;
      if (value === null || value === '') return null;

      const regex = /^\d{1,3}(\.\d{1,2})?$/;
      return regex.test(value) ? null : { invalidNumber: true };
    };
  }

  onImageSelect({ target }: any): void {
    const file = target.files?.[0];
    if (
      !file ||
      !['image/png', 'image/jpeg', 'image/jpg', 'image/gif'].includes(
        file.type,
      ) ||
      file.size > 2 * 1024 * 1024
    ) {
      alert('Allowed formats: PNG, JPG, JPEG, GIF. Max size: 2MB');
      return;
    }

    this.uploadedFile = file;
    const reader = new FileReader();
    reader.onload = () => (this.previewImage = reader.result as string);
    reader.readAsDataURL(file);
  }

  onSubmit() {
    this.spinner.show();
    if (this.userProfileForm.invalid) {
      this.userProfileForm.markAllAsTouched();
      this.spinner.hide();
      return;
    }

    const formValue = this.userProfileForm.value;
    let selectedCountry;
    let selectedState;
    let dobControl;
    let emailControl;
    if (formValue.country && formValue.state) {
      selectedCountry = this.countryDetails.find(
        (c: any) => c.country_id === parseInt(formValue.country),
      );
      selectedState = selectedCountry.states.find(
        (c: any) => c.state_id === parseInt(formValue.state),
      );
    }
    if (this.userProfileForm.get('dob')) {
      dobControl = this.userProfileForm.get('dob');
    }
    if (this.userProfileForm.get('email')) {
      emailControl = this.userProfileForm.get('email');
    }
    if (
      !formValue.first_name ||
      !formValue.last_name ||
      !formValue.address ||
      !formValue.gender ||
      !formValue.city ||
      !formValue.blood_group ||
      !selectedCountry.country_id ||
      !formValue.email ||
      (formValue.email && emailControl && emailControl?.invalid) ||
      !formValue.height ||
      (formValue.alt_phone_number &&
        !(
          (formValue.alt_phone_number.startsWith('0') &&
            formValue.alt_phone_number.length === 10) ||
          (!formValue.alt_phone_number.startsWith('0') &&
            formValue.alt_phone_number.length === 9)
        )) ||
      !selectedState.state_id ||
      !formValue.weight ||
      !formValue.dob ||
      (formValue.dob && dobControl?.errors?.futureDate) ||
      !formValue.phone?.number ||
      !formValue.phone.number?.replace(/\s/g, '') ||
      formValue.phone.number?.replace(/\s/g, '').length !== 9 ||
      !formValue.phone.dialCode
    ) {
      this.userProfileForm.markAllAsTouched();
      this.spinner.hide();
      return;
    }

    const formData = new FormData();
    formData.append('address', formValue.address || '');
    formData.append('first_name', formValue.first_name || '');
    formData.append('last_name', formValue.last_name || '');
    formData.append('alt_phone_number', formValue.alt_phone_number || '');
    formData.append('blood_group', formValue.blood_group || '');
    formData.append('city', formValue.city || '');
    formData.append('gender', formValue.gender || '');
    formData.append('country', selectedCountry.country_id || '');
    formData.append('dob', formValue.dob || '');
    formData.append('email', formValue.email || '');
    formData.append('height', formValue.height || '0');
    formData.append('landmark', formValue.landmark || '');
    formData.append('phone', formValue.phone.number.replace(/\s/g, '') || '');
    formData.append('country_dial_code', formValue.phone.dialCode);
    formData.append('profile', this.uploadedFile ? this.uploadedFile : null);
    formData.append('state', selectedState.state_id || '');
    formData.append('weight', formValue.weight || '0');

    if (this.isOpenFromPopup) {
      this.updateAssignCaregiver();
    } else {
      this.updateUserProfile(formData);
    }
  }

  updateUserProfile(formData: any) {
    this.apiService.updateProfile(formData).subscribe(
      (res: any) => {
        this.spinner.hide();
        const id = JSON.parse(localStorage.getItem('user') || '{}').user_id;
        this.getUserProfile(id);

        this.tosterService.success('Profile updated successfully', '', {
          timeOut: 4000,
        });
      },
      (err: any) => {
        this.spinner.hide();
        this.tosterService.error('Error updating profile', '', {
          timeOut: 4000,
        });
        console.error('Error updating profile:', err);
      },
    );
  }

  updateAssignCaregiver() {
    const payload = this.buildPayload();
    this.apiService.updateDependent(payload).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        if (res.success) {
          this.commonService.closeProfileModal();
          this.commonService.openSuccessMemberModal(res.data);
        } else {
          const message = res.message || '';
          this.tosterService.error(message || 'Something went wrong', '', {
            timeOut: 4000,
          });
        }
      },

      error: (err) => {
        this.spinner.hide();

        const message =
          err?.error?.message || err?.error?.error || 'Something went wrong';

        this.tosterService.error(message || 'Something went wrong', '', {
          timeOut: 4000,
        });
      },
    });
  }

  buildPayload(): any {
    const formValue = this.userProfileForm.value;

    const payload: any = {
      first_name: formValue.first_name,
      last_name: formValue.last_name,
      dob: formValue.dob,
      blood_group: formValue.blood_group,
      email: formValue.email,
      height: formValue.height,
      weight: formValue.weight,
      gender: formValue.gender,
      address: formValue.address,
      landmark: formValue.landmark,
      city: formValue.city,
      state: formValue.state,
      country: formValue.country,
      phone: formValue.phone?.number?.replace(/\s/g, '') || '',
    };

    if (formValue.alt_phone_number) {
      payload.alt_phone_number = formValue.alt_phone_number;
    }

    if (this.assignedCareGiver) {
      payload.dependent_id = this.userProfile?.user_id;
      payload.caregiver_id = this.assignedCareGiver?.caregiver_id;
      payload.duration = this.assignedCareGiver?.duration;
      payload.event_type = this.assignedCareGiver?.event_type;
      payload.phone_number = this.assignedCareGiver?.phone_number;
      payload.country_dial_code = this.assignedCareGiver?.country_dial_code;
    }

    return payload;
  }

  getAllCountries(selectedUserId?: any) {
    this.spinner.show();
    this.apiService.getAllCountries().subscribe(
      (res: any) => {
        this.countryDetails = res.data;
        const id =
          selectedUserId ||
          JSON.parse(localStorage.getItem('user') || '{}').user_id;

        this.apiService.getUserProfileRecord(id).subscribe(
          (res: any) => {
            this.userProfile = res.data || {};
            if (this.userProfile.country) {
              const selectedCountryId = parseInt(this.userProfile.country);
              const selectedCountry = this.countryDetails.find(
                (c: any) => parseInt(c.country_id) === selectedCountryId,
              );
              if (selectedCountry) {
                this.selectedCountryStates = selectedCountry.states || [];
              } else {
                this.selectedCountryStates = [];
              }

              this.selectedStateCities = [];
              this.userProfileForm.patchValue({
                state: parseInt(this.userProfile.state),
                city: '',
              });
              if (this.userProfile.state) {
                const selectedStateId = parseInt(this.userProfile.state);
                const selectedState = this.selectedCountryStates.find(
                  (s) => parseInt(s.state_id) === selectedStateId,
                );
                if (selectedState) {
                  this.selectedStateCities = selectedState.cities || [];
                } else {
                  this.selectedStateCities = [];
                }

                this.userProfileForm.patchValue({
                  city: this.userProfile.city,
                });
              }
            }
            this.spinner.hide();
          },
          (err: any) => {
            console.error('Error fetching profile:', err);
            this.userProfile = {};
            this.spinner.hide();
          },
        );
      },
      (err: any) => {
        console.error('Error fetching country:', err);
        this.spinner.hide();
      },
    );
  }

  getUserProfile(userId: any) {
    this.spinner.show();

    this.apiService.getUserProfileRecord(userId).subscribe(
      (res: any) => {
        this.userProfile = res.data || {};
        this.setUserProfile();
      },
      (err: any) => {
        console.error('Error fetching profile:', err);
        this.userProfile = {};
        this.initializeForm(this.userProfile);
        this.spinner.hide();
      },
    );
    this.spinner.hide();
  }

  setUserProfile(setInlocal: boolean = true) {
    if (setInlocal) {
      localStorage.setItem('user', JSON.stringify(this.userProfile));
    }

    this.initializeForm(this.userProfile);

    this.assignedCareGiver = null;
    if (
      this.userProfile?.caregiverInfo &&
      this.userProfile?.caregiverInfo?.first_name
    ) {
      this.assignedCareGiver = this.userProfile?.caregiverInfo;
      this.assignedCareGiver['age'] = this.getAge(this.assignedCareGiver?.dob);
    }
    this.spinner.hide();
    this.commonService.updateProfile();
  }

  isNineDigitNumber(value: any): boolean {
    const num = Number(value);
    return Number.isInteger(num) && num >= 100000000 && num <= 999999999;
  }

  isValidDob = (dob: string | null | undefined): boolean => {
    if (!dob || dob === '0000-00-00') return false;
    const parsed = new Date(dob);
    return !isNaN(parsed.getTime());
  };

  initializeForm(user: any) {
    const isFakeFirstName = this.isSystemGeneratedName(user?.first_name);
    const isFakeLastName = this.isSystemGeneratedName(user?.last_name);

    this.userProfileForm.patchValue({
      first_name: isFakeFirstName ? '' : user?.first_name || '',
      last_name: isFakeLastName ? '' : user?.last_name || '',
      dob: this.isValidDob(user?.dob) ? user?.dob : '',
      blood_group: user?.blood_group || '',
      email: user?.email || '',
      height: user?.height || '',
      gender: user?.gender || '',
      weight: user?.weight || '',
      phone: user?.phone ?? null,
      alt_phone_number: this.isNineDigitNumber(user?.alt_phone_number)
        ? user?.alt_phone_number
        : '',
      address: user?.address || '',
      landmark: user?.landmark || '',
      city: user?.city || '',
      state: user?.state || '',
      country: user?.country || '',
    });
    this.userProfileForm.get('phone')?.valueChanges.subscribe(() => {
      this.userProfileForm
        .get('phone')
        ?.updateValueAndValidity({ emitEvent: false });
    });
    this.userProfileForm.get('alt_phone_number')?.valueChanges.subscribe(() => {
      this.userProfileForm
        .get('alt_phone_number')
        ?.updateValueAndValidity({ emitEvent: false });
    });

    if (this.isValidDob(user?.dob)) {
      this.dobValue = new Date(user.dob);
      this.isDobDisabled = true;
    }
  }

  getFormattedDobWithAge(dob: string): string {
    if (!dob || dob === '0000' || dob.trim() === '') {
      return '';
    }

    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const birthDate = new Date(dob);

    if (isNaN(birthDate.getTime()) || birthDate.getFullYear() === 1970) {
      return '';
    }

    const today = new Date();
    const day = birthDate.getDate();
    const month = months[birthDate.getMonth()];
    const year = birthDate.getFullYear();

    let age = today.getFullYear() - year;
    const hasBirthdayPassed =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= day);

    if (!hasBirthdayPassed) {
      age -= 1;
    }

    return `${day} ${month} ${year}, ${age} years`;
  }

  onCountryChange(event: any) {
    const selectedCountryId = parseInt(event.target?.value || event);
    const selectedCountry = this.countryDetails.find(
      (c: any) => c.country_id === selectedCountryId,
    );

    if (selectedCountry) {
      this.selectedCountryStates = selectedCountry.states || [];
    } else {
      this.selectedCountryStates = [];
    }

    this.selectedStateCities = [];
    this.userProfileForm.patchValue({ state: '', city: '' });
  }

  onStateChange(event: any) {
    const selectedStateId = parseInt(event.target?.value || event);
    const selectedState = this.selectedCountryStates.find(
      (s) => s.state_id === selectedStateId,
    );

    if (selectedState) {
      this.selectedStateCities = selectedState.cities || [];
    } else {
      this.selectedStateCities = [];
    }

    this.userProfileForm.patchValue({ city: '' });
  }

  checkEnabledDisbaled() {
    return (
      JSON.parse(localStorage.getItem('user') || '{}').role === 'Dependent'
    );
  }

  shouldDisableField(fieldName: string): boolean {
    const value = this.userProfile?.[fieldName];
    const role = JSON.parse(localStorage.getItem('user') || '{}').role;

    if (
      (fieldName === 'first_name' || fieldName === 'last_name') &&
      this.isSystemGeneratedName(value)
    ) {
      return false;
    }

    if (role === 'Dependent') {
      if (
        ['phone', 'alt_phone_number', 'email', 'height', 'weight'].includes(
          fieldName,
        )
      ) {
        return value != null && value !== '';
      }
      return (
        ['first_name', 'last_name', 'gender', 'dob', 'blood_group'].includes(
          fieldName,
        ) &&
        (fieldName === 'dob'
          ? this.isValidDob(value)
          : value != null && value !== '')
      );
    }

    return (
      [
        'first_name',
        'last_name',
        'gender',
        'dob',
        'blood_group',
        'phone',
      ].includes(fieldName) &&
      (fieldName === 'dob'
        ? this.isValidDob(value)
        : value != null && value !== '')
    );
  }

  checkEligibility() {
    this.eligibilityStatus = 'INACTIVE';
    const userDetail = JSON.parse(localStorage.getItem('user') || '{}');
    this.activeDays = 0;

    this.apiService
      .checkUserEligibility({ user_id: userDetail.user_id })
      .subscribe(
        (res: any) => {
          const success = !!res?.success;
          const active = !!res?.active;
          this.activeDays = Number(res?.totalActiveDays) || 0;

          if (success && active) {
            this.eligibilityStatus = 'ELIGIBLE';
          } else if (!success && !active) {
            this.eligibilityStatus = 'INACTIVE';
          } else {
            this.eligibilityStatus = 'INELIGIBLE';
          }
        },
        () => {
          this.tosterService.error('Error checking eligibility', '', {
            timeOut: 4000,
          });
        },
      );
  }

  isSystemGeneratedName(value: string | null | undefined): boolean {
    if (!value) return false;

    const invalidNames = [
      'ambulance',
      'user',
      'ambulance user',
      'ussd ussd',
      'ivr ivr',
      'ussd',
      'ivr',
    ];
    return invalidNames.includes(value.toLowerCase().trim());
  }

  noAllZerosPhoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      let digits = '';

      if (typeof control.value === 'object' && control.value.nationalNumber) {
        digits = control.value.nationalNumber.replace(/\D/g, '');
      } else if (typeof control.value === 'string') {
        digits = control.value.replace(/\D/g, '');
      }

      if (!digits) return null;

      if (/^0+$/.test(digits)) {
        return { allZeros: true };
      }

      return null;
    };
  }

  openAssignCaregiverModal() {
    this.commonService.triggerCaregiverModal();
  }

  resetFormValidation() {
    if (this.userProfileForm) {
      this.userProfileForm.markAsPristine();
      this.userProfileForm.markAsUntouched();
      this.userProfileForm.updateValueAndValidity();
    }
  }

  getAge(dob: string | Date): number | null {
    if (!dob) return null;

    const birthDate = new Date(dob);

    if (isNaN(birthDate.getTime())) return null;

    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const month = today.getMonth() - birthDate.getMonth();

    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age : null;
  }

  getProfileImage(): string {
    const preview = this.previewImage?.trim();
    const profile = this.userProfile?.profile?.trim();

    if (preview) {
      return preview;
    }

    if (profile && profile !== 'null') {
      return profile;
    }

    return '../../../../assets/img/patient-avtar.png';
  }

  onImgError(event: any) {
    event.target.src = '../../../../assets/img/patient-avtar.png';
  }
}
